document.addEventListener("DOMContentLoaded", (event) => {
  /*
  * Root element to manage product configurator state
  */
  class PkStore extends HTMLElement {
    constructor() {
      super();
      const savedState = localStorage.getItem('pkStoreState');
      this.state = savedState ? JSON.parse(savedState) : this.getInitialState();
      this.listeners = [];
    }

    getInitialState() {
      return {
        currentStep: 1,
        selectedMethod: null,
        selectedFlavours: [],
        sensoryValues: {},
        propertyValues: {},
        metaProducts: [],
        filteredMetaProducts: [],
        customProductName: ""
      };
    }

    connectedCallback() {
      this.addEventListener('step-change', (e) => this.handleStepChange(e.detail));
      this.addEventListener('reset-config', () => this.resetState());
      this.initMetaProductList();
    }

    // Adds a listener to the list of state change listeners
    subscribe(listener) {
      this.listeners.push(listener);
      listener(this.state);
    }

    // Updates the state and calls the provided callback (if any)
    updateState(newState, callback) {
      this.state = { ...this.state, ...newState };
      localStorage.setItem('pkStoreState', JSON.stringify(this.state)); // Save state to local storage
      this.listeners.forEach(listener => listener(this.state));      
      
      if (callback) callback();
    }

    // Resets the state to the initial values
    resetState() {
      const initialState = this.getInitialState();      
      
      this.updateState(initialState, () => {
        this.initMetaProductList();
      });
    }

    // To reset and use default SVG on first render (or in a step)
    resetSvgToDefault() {
      const svgElement = document.querySelector('#pk-svg');
      if (svgElement) {
        svgElement.innerHTML = defaultSVGContent;
      }
    }

    // Handles the step-change event
    handleStepChange(detail) {
      const { nextStep, ...rest } = detail;
      this.updateState({ currentStep: nextStep, ...rest }, () => {
        console.log(`State at Step ${nextStep}:`, this.state); // Log current state for work
        
        if (nextStep === 5) this.updateCustomProductName();
        this.filterProducts(nextStep);
      });
    }

    initMetaProductList() {
      const globalMetaProducts = metaProducts.metaProducts;

      if (Array.isArray(globalMetaProducts)) {
        this.updateState({
          metaProducts: globalMetaProducts,
          filteredMetaProducts: globalMetaProducts
        }, () => {
          this.filterProducts(this.state.currentStep);
        });
      } else {
        console.error("globalMetaProducts is not a valid array.");
      }
    }

    // Filters the products based on the current step
    filterProducts(step) {
      const productsToFilter = this.state.metaProducts;

      const filter = new FilterMetaProductsBySteps(productsToFilter, this.state, step);
      const filteredProducts = filter.filter();

      this.updateState({ filteredMetaProducts: filteredProducts });
    }

    // Updates the custom product name based on the filtered products
    updateCustomProductName() {
      const customProductName = this.getCustomProductName();
      
      if (customProductName) {
        this.updateState({ customProductName }, () => {
          this.notifyCustomProductName();
        });
      }
    }

    // Retrieves the custom product name if there is only one filtered product
    getCustomProductName() {
      if (this.state.filteredMetaProducts.length === 1) {
        return this.state.filteredMetaProducts[0].name;
      }
      
      return null;
    }

    // Dispatches an event with the custom product name
    notifyCustomProductName() {
      const customProductName = this.state.customProductName;
      
      if (customProductName) {
        this.dispatchEvent(new CustomEvent('custom-product-name-updated', {
          detail: customProductName,
          bubbles: true,
          composed: true
        }));
      }
    }
  }
  customElements.define('pk-store', PkStore);

  const defaultSVGContent = `
    <svg height="630px" width="380px" style="background-color: white;" version="1.1" id="Calque_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 438 736" xml:space="preserve">
      <style type="text/css">
        .st0 { fill: none; stroke: #020202; stroke-miterlimit: 10; }
        .st1 { fill: none; stroke: #000000; stroke-miterlimit: 10; }
        .st2 { fill: #1D1D1B; }
        .st3 { fill: none; stroke: #1D1D1B; stroke-miterlimit: 10; }
        .st7 { font-family: 'Media77-Regular'; }
        .st8 { font-size: 14.087px; fill: white; }
        .st9 { font-family: 'MMSSans-Regular'; }
        .st10 { font-size: 25px; fill: white; }
        .hidden-shape { display: none; }
        #full-1 { fill: #cbc09e; }
        #full-2 { fill: #8e9358; }
        #full-3 { fill: #f2F2F2; }
      </style>
      
      <!-- Hidden Paths -->
      <path id="bottom-13" class="st0 hidden-shape" d="M62.5,500.9c27.8,40.2,92,40.2,157.4,40.2c37.8,0,118.5,4,154.4-50.5c6.2-9.3,11-20.4,14.1-33.5c4-17.2,8.7-38.7-1.9-60.6c-12-25-134.5-21.7-153.2-21.7c-85.7,1-137.2,0.2-163.9,16c-16.1,9.4-23.2,24.8-23.5,50.1C45.9,462.9,52.2,485.9,62.5,500.9z"></path>
      <path id="top-13" class="st1 hidden-shape" d="M221.1,42c-52,0.7-115.7-5-152.9,38.7c-19.1,22.8-20.1,45.5-18.5,73.8c3.9,43.8,35,55.1,74.4,55.1c37.8-0.8,106,2.4,163.9-1.7c40.9-2.9,76.6-9.4,92.8-23.4c12.5-14.8,16.4-34.3,15.8-45.8c-1.2-25.7-4.5-45.7-16.4-60.8C360.6,52.8,317.3,41.8,221.1,42z"></path>
      <path id="center-13" class="st1 hidden-shape" d="M397.6,284.1c0-48.7-11.1-52-25.4-58.2c-26-11.3-73.6-15.7-129.7-15.7c-64.2,0-151-7.2-182.6,24.1c-10.4,10.3-14.7,40.7-15.1,66.8c-0.2,12.5-1.9,43.6,7.3,56.7c24.2,26.4,125.6,16.6,162.5,17.1c91,1.1,136.8-5.7,159.8-19.9C397.4,340.7,397.6,318,397.6,284.1z"></path>
      <path id="top-24" class="st3 hidden-shape" d="M396.4,171L396.4,171c0-36,0-63.7-10.8-84C369,55.8,327,42,220.8,42.3c-66.5-2.2-119.7,2.2-149.4,34.9C53.5,96.7,44,126.5,45.2,171c0.6,46.5,3.8,75,15,92.6c19.6,30.5,63.5,27.6,160.6,27.6c64,0,112.2-4.8,141.8-27.7C385.6,245.7,397.3,216.9,396.4,171"></path>
      <path id="center-24" class="st3 hidden-shape" d="M220.2,417c-84,0-135.2,1.6-159-18.3c-10.8-9-16-22.5-16-42.5c0-27.6,0.2-43.2,14.5-52.1c18.8-11.6,61.8-11.6,160.4-11.6c97.2,0,140.8,0,160.3,10.5c15.4,8.2,15.8,22.9,15.8,49c0,16.3-3.2,28.4-9.9,37.6C366.3,417,314.3,417,220.2,417"></path>
      <path id="bottom-24" class="st3 hidden-shape" d="M376.79,428.94c-6-15.89-39.29-11.44-57.06-11.95-39.25-1.12-78.53,0-117.79.07-31.9,0-62.57.1-95.63-1.17-27.37-2.59-44.67,13.53-50.82,34.31-6.85,23.14.12,52.06,22.37,67.43C119.54,545,172.77,541,220.54,541.84c46.37,1.22,106.12-3.91,138-32.48,19.11-17.14,28.18-42.72,18.28-80.42"></path>
      <path id="top-12" class="st3 hidden-shape" d="M45.3,165.8c0-33.9,6-58.7,17-76.8C89.4,44.6,147,40.7,221,42.3c132.4-0.4,165.1,21.2,173.1,71c3,18.3,2.6,40.5,2.6,66.7c-0.8,24.3-3.1,48.9-20.1,68.4c-43.1,49.4-96.2,40.9-162.9,43.3c-68.1,2.5-133.8-9.6-150.3-42.5C49.9,223.7,46.2,194.3,45.3,165.8z"></path>
      <path id="bottom-12" class="st3 hidden-shape" d="M396.5,367.7c7.4-86.7-98-73.9-208.6-75.4c-79.7-4-144.3,1.4-142.7,99.2c0.6,23.2-0.2,43,1.3,61s5.6,34,15.9,49.7c29.5,43,115.3,39.8,158.2,39.8c44.1,0,117.3-1.8,146.9-41.4c9-12.6,15.7-26.5,20.4-42.4C395.2,433.5,397.6,404.2,396.5,367.7z"></path>
      
      <!-- Visible Paths -->
      <path id="full-1" class="st2" d="M221.1,42.8C116.9,40.5,45.2,49.3,45.2,166.4c0,117.1,0.2,193.5,0.2,250.4c0,37.6,2.7,65.3,17,85c29.5,43,115.4,39.8,158.4,39.8c44.2,0,117.4-1.8,147-41.4c16-22.7,29.1-55.3,29-109.6c-0.1-70.2,0.1-189.4,0.1-212.1C396.9,82.1,401.1,42.3,221.1,42.8"></path>
      <path id="full-2" class="st2" d="M221.1,42.8C116.9,40.5,45.2,49.3,45.2,166.4c0,117.1,0.2,193.5,0.2,250.4c0,37.6,2.7,65.3,17,85c29.5,43,115.4,39.8,158.4,39.8c44.2,0,117.4-1.8,147-41.4c16-22.7,29.1-55.3,29-109.6c-0.1-70.2,0.1-189.4,0.1-212.1C396.9,82.1,401.1,42.3,221.1,42.8"></path>
      <path id="full-3" class="st2" d="M221.1,42.8C116.9,40.5,45.2,49.3,45.2,166.4c0,117.1,0.2,193.5,0.2,250.4c0,37.6,2.7,65.3,17,85c29.5,43,115.4,39.8,158.4,39.8c44.2,0,117.4-1.8,147-41.4c16-22.7,29.1-55.3,29-109.6c-0.1-70.2,0.1-189.4,0.1-212.1C396.9,82.1,401.1,42.3,221.1,42.8"></path>
    
      <!-- Additional Paths -->
      <path d="M351.9,651.9c4.5,1.2,9.6,1.7,13.5,1.7c15.3,0,25.8-7.8,25.8-19.7c0-10.1-7.5-14-14.1-16.7l-8.7-3.6c-6.8-2.8-10.3-6.5-10.3-12c0-8,7.5-11.7,15.9-11.7c4.4,0,8.4,1,12.7,2.7h0.2V588c-3.7-1.2-8.3-1.7-12.3-1.7c-13,0-23.5,5.9-23.5,17.7c0,9.4,6.8,13.9,14.2,17l8.4,3.6c5.8,2.5,10.4,5.8,10.4,12.3c0,8.8-7.9,13.1-17.8,13.1c-5,0-9.7-1.2-14.2-2.9h-0.2V651.9z"></path>
      <path d="M315.1,653.9c16.5,0,25.7-9.8,25.7-24v-42.4h-4.4v41.4c0,13.5-8.4,19.7-19.3,19.7c-12,0-19.2-7.1-19.2-19.7v-41.4h-8.6v42.4C289.4,644.4,298.9,653.9,315.1,653.9"></path>
      <polygon points="242.2,638.9 220.4,587.5 209.1,587.5 209.1,652.6 213.5,652.6 213.5,591.5 239.6,653.1 240.7,653.1 266.9,591.5 266.9,652.6 275.2,652.6 275.2,587.5 264.1,587.5 "></polygon>
      <path d="M183.2,591.8c-5.6-3.7-12.4-5.8-19.9-5.8c-7.5,0-14.3,2.1-19.9,5.8c-8.7,5.8-14.2,14.9-14.2,28.1c0,13.2,5.6,22.3,14.2,28.1c5.6,3.7,12.4,5.8,19.9,5.8c7.5,0,14.3-2.1,19.9-5.8c8.7-5.8,14.2-14.9,14.2-28.1C197.4,606.7,191.9,597.7,183.2,591.8z M163.3,650c-15.1,0-25.4-12.5-25.4-30s10.2-30,25.4-30c15.1,0,25.4,12.5,25.4,30S178.5,650,163.3,650z"></path>
      <polygon points="84.6,638.9 62.8,587.5 51.5,587.5 51.5,652.6 55.8,652.6 55.8,591.5 81.9,653.1 83,653.1 109.2,591.5 109.2,652.6 117.5,652.6 117.5,587.5 106.5,587.5 "></polygon>
      <path d="M273.3,674.9c-0.9-0.2-1.6-0.7-3.9-0.7c-3.1,0-4.8,1.9-4.8,3.8c0,4.5,7.2,3.1,7.2,6.6c0,1.3-1,2.4-3,2.4c-2.2,0-3.3-1.6-3.4-3.3l-0.9,0.1c0.2,1.3,0.2,2.4,0.2,3.3c1.2,0.5,2.7,0.7,4.2,0.7c2.5,0,5.3-1,5.3-3.9c0-4.8-7.3-3.4-7.3-6.7c0-1.2,1.1-2.2,2.7-2.2c2.7,0,3.1,2.4,3.1,3.2l0.9-0.1C273.3,676.9,273.3,676.2,273.3,674.9"></path>
      <path d="M258.6,670.3c0.9-0.7,1.2-1.4,1.2-1.8c0-0.5-0.6-0.8-1-0.8c-1.3,0-2.5,2.5-3.7,4.5l0.6,0.6L258.6,670.3z"></path>
      <g>
        <path d="M257.4,686.9c-2.4,0-3.8-1.9-3.8-5.9h5.3c2.5,0,3.3-0.1,3.3-1.4c0-1.9-1.2-5.4-5.3-5.4c-3.6,0-5.9,2.8-5.9,6.8c0,3.9,2.3,6.8,6.1,6.8c2.7,0,4.2-1.3,5.2-3.4l-0.9-0.3C260.4,686.2,258.7,686.9,257.4,686.9z M256.8,675c1.6,0,2.7,1.5,2.7,3.1c0,1.6-0.2,2-3.2,2h-2.8C253.6,677.7,254.6,675,256.8,675z"></path>
        <path d="M248.1,667.8c-4.1,0-4.3,3.6-4.3,4.8v2h-2.5v0.9h2.5v8.4c0,2.3-0.2,2.5-1.2,2.5c-0.2,0-0.5,0-0.7,0v0.9c1-0.1,2.2-0.2,3.3-0.2c1.2,0,2.3,0.1,3.4,0.2v-0.9c-0.2,0-0.8,0-1,0c-1.4,0-1.4-0.4-1.4-2.5v-8.4h3.2l0-0.9h-3.2v-1c0-1.4-0.2-2.6-0.2-3.6c0-0.8,0.5-1.4,1.3-1.4c1.9,0,0.4,2.4,2.5,2.4c0.7,0,1.2-0.4,1.2-1.3C251.1,668.5,249.6,667.8,248.1,667.8z"></path>
        <path d="M238.9,686.4c-0.8,0-0.9-0.9-0.9-1.8v-5.9c0-2.8-1.5-4.5-5-4.5c-3,0-4.8,1.9-4.8,3.2c0,1.4,0.9,1.6,1.4,1.6c0.7,0,1.3-0.4,1.3-1.3c0-0.4-0.2-0.8-0.2-1.3c0-0.8,0.8-1.3,2.2-1.3c2.2,0,2.6,1.7,2.6,3.3v1.7c-6,0-7.9,2.2-7.9,4.1c0,1.4,0.7,3.5,4.1,3.5c3.5,0,4-2.7,4-2.7l0.1,0c0,1.8,0.9,2.7,2.1,2.7c0.9,0,1.9-0.5,2.7-1.6l-0.6-0.6C240.2,685.5,239.7,686.4,238.9,686.4z M235.7,682.5c0,1.6-0.7,4.3-3,4.3c-2.2,0-2.2-2.5-2.2-2.6c0-2.8,2.4-3.3,5.3-3.3V682.5z"></path>
      </g>
      <path d="M225.2,668.3c-1.2-0.3-3-0.7-5.5-0.7c-5.6,0-9.7,3.6-9.7,10c0,6.5,3.7,10.2,9.1,10.2c3,0,5.5-1.3,7-4.2l-0.9-0.4c-0.9,2.1-3.1,3.7-5.5,3.7c-4.2,0-6.8-3.4-6.8-9.2c0-6.2,3-9.1,6.9-9.1c2.2,0,4.7,1,4.8,4.9l1-0.1C225.3,671.6,225.2,670.3,225.2,668.3"></path>
      <g>
        <path d="M188.1,686.9c-2.4,0-3.8-1.9-3.8-5.9h5.3c2.5,0,3.3-0.1,3.3-1.4c0-1.9-1.2-5.4-5.3-5.4c-3.6,0-5.9,2.8-5.9,6.8c0,3.9,2.3,6.8,6.1,6.8c2.7,0,4.2-1.3,5.2-3.4L192,684C191.1,686.2,189.4,686.9,188.1,686.9z M187.6,675c1.6,0,2.7,1.5,2.7,3.1c0,1.6-0.2,2-3.2,2h-2.8C184.3,677.7,185.4,675,187.6,675z"></path>
        <path d="M177.8,683.6v-15.8c-1.3,0.4-2.1,0.4-3.3,0.4c-0.5,0-1.2,0-1.6-0.1v0.9c0.4-0.1,0.8-0.1,1.1-0.1c1.3,0,1.4,0.3,1.4,2v1.9c0,0.7,0,2.6,0.3,3.6l-0.1,0c-0.7-1.4-2-2.1-3.6-2.1c-3.2,0-5.7,2.7-5.7,6.9c0,4.1,2.4,6.4,5.2,6.4c2.7,0,3.9-2.2,4.1-3.2l0.1,0c0,1.5,0.2,2.4,0.3,3.2c1-0.3,2.3-0.5,3.5-0.5c0.3,0,0.6,0,0.9,0.1v-0.9c-0.4,0.1-0.8,0.1-1.1,0.1C178.2,686.5,177.8,686,177.8,683.6z M172,686.7c-2.2,0-3.1-2.3-3.1-5.1c0-3.5,1.4-6.4,3.5-6.4c2.4,0,3.1,2.8,3.1,5.6C175.5,684.3,174,686.7,172,686.7z"></path>
      </g>
      <path d="M103.5,671.2c1.1,0,1.7-0.8,1.7-1.7c0-0.8-0.6-1.7-1.7-1.7s-1.7,0.8-1.7,1.7C101.8,670.3,102.4,671.2,103.5,671.2"></path>
      <path d="M86.2,671.2c1.1,0,1.7-0.8,1.7-1.7c0-0.8-0.6-1.7-1.7-1.7c-1.1,0-1.7,0.8-1.7,1.7C84.5,670.3,85.1,671.2,86.2,671.2"></path>
      <g>
        <path d="M141.9,677.3c0-1.2,1.1-2.2,2.7-2.2c2.7,0,3.1,2.4,3.1,3.2l0.9-0.1c-0.3-1.2-0.3-1.9-0.3-3.3c-0.9-0.2-1.6-0.7-3.9-0.7c-3.1,0-4.8,1.9-4.8,3.8c0,4.5,7.2,3.1,7.2,6.6c0,1.3-1,2.4-3,2.4c-2.2,0-3.3-1.6-3.4-3.3l-0.9,0.1c0.2,1.3,0.2,2.4,0.2,3.3c1.2,0.5,2.7,0.7,4.2,0.7c2.5,0,5.3-1,5.3-3.9C149.3,679.2,141.9,680.6,141.9,677.3z"></path>
        <path d="M135.8,684v-4.9c0-2.2-0.6-4.9-4.2-4.9c-2.8,0-4.2,2.2-4.3,3.1l-0.1,0c0-1.5-0.1-2.3-0.3-3c-1.1,0.3-2.1,0.4-3.1,0.4c-0.4,0-0.8,0-1.2-0.1v0.9c0.4-0.1,0.8-0.1,1.1-0.1c1.2,0,1.6,0.5,1.6,2.9v5.6c0,2.3-0.2,2.5-1.2,2.5c-0.2,0-0.5,0-0.7,0v0.9c1-0.1,2.2-0.2,3.3-0.2c1.2,0,2.3,0.1,3.4,0.2v-0.9c-0.2,0-0.8,0-1,0c-1.4,0-1.4-0.4-1.4-2.5v-3.2c0-4.3,1.9-5.6,3.3-5.6c1.8,0,2.5,1.3,2.5,4.5v4.3c0,2.3-0.2,2.5-1.2,2.5c-0.2,0-0.5,0-0.7,0v0.9c1-0.1,2.2-0.2,3.3-0.2c1.2,0,2.3,0.1,3.4,0.2v-0.9c-0.2,0-0.8,0-1,0C135.8,686.5,135.8,686.1,135.8,684z"></path>
        <path d="M114.8,674.2c-3.6,0-6.1,2.7-6.1,6.8c0,4.1,2.6,6.8,6.1,6.8c3.6,0,6.2-2.7,6.2-6.8C121,676.9,118.4,674.2,114.8,674.2z M114.8,687c-2.4,0-3.5-2.3-3.5-6s1.1-6,3.5-6c2.4,0,3.5,2.3,3.5,6S117.2,687,114.8,687z"></path>
        <path d="M104.7,684v-9.7c-1.3,0.4-2.4,0.4-3.6,0.4c-0.4,0-0.9,0-1.3-0.1v0.9c0.4-0.1,0.8-0.1,1.1-0.1c1.3,0,1.4,0.3,1.4,2v6.5c0,2.3-0.2,2.5-1.2,2.5c-0.2,0-0.5,0-0.7,0v0.9c1-0.1,2.2-0.2,3.3-0.2c1.2,0,2.3,0.1,3.4,0.2v-0.9c-0.2,0-0.8,0-1,0C104.7,686.5,104.7,686.1,104.7,684z"></path>
        <path d="M96.4,686.7c-1,0-1.2-1.2-1.2-2.5v-8.6h3.3v-0.9h-3.3v-4.1h-0.9c0,2.9-0.8,4.1-3.8,4.1v0.9h2.3v8.7c0,2.3,1,3.5,2.8,3.5c1.5,0,2.7-0.9,3.6-2.6l-0.7-0.5C97.8,686.1,97.2,686.7,96.4,686.7z"></path>
        <path d="M87.4,684v-9.7c-1.3,0.4-2.4,0.4-3.6,0.4c-0.4,0-0.9,0-1.3-0.1v0.9c0.4-0.1,0.8-0.1,1.1-0.1c1.3,0,1.4,0.3,1.4,2v6.5c0,2.3-0.2,2.5-1.2,2.5c-0.2,0-0.5,0-0.7,0v0.9c1-0.1,2.2-0.2,3.3-0.2c1.2,0,2.3,0.1,3.4,0.2v-0.9c-0.2,0-0.8,0-1,0C87.4,686.5,87.4,686.1,87.4,684z"></path>
        <path d="M79.2,683.6v-15.8c-1.3,0.4-2.1,0.4-3.3,0.4c-0.5,0-1.2,0-1.6-0.1v0.9c0.4-0.1,0.8-0.1,1.1-0.1c1.3,0,1.4,0.3,1.4,2v1.9c0,0.7,0,2.6,0.3,3.6l-0.1,0c-0.7-1.4-2-2.1-3.6-2.1c-3.2,0-5.7,2.7-5.7,6.9c0,4.1,2.4,6.4,5.2,6.4c2.7,0,3.9-2.2,4.1-3.2l0.1,0c0,1.5,0.2,2.4,0.3,3.2c1-0.3,2.3-0.5,3.5-0.5c0.3,0,0.6,0,0.9,0.1v-0.9c-0.4,0.1-0.8,0.1-1.1,0.1C79.6,686.5,79.2,686,79.2,683.6z M73.4,686.7c-2.2,0-3.1-2.3-3.1-5.1c0-3.5,1.4-6.4,3.5-6.4c2.4,0,3.1,2.8,3.1,5.6C76.9,684.3,75.4,686.7,73.4,686.7z"></path>
        <path d="M58.4,686.4c-2.8,0-2.8-0.4-2.8-2.3v-6.3c0.6-0.1,1.2-0.1,1.7-0.1c3.4,0,3.5,1.1,3.5,2.9l0.9-0.1c-0.1-1.1-0.2-2.2-0.2-3.3c0-1,0.1-2.1,0.2-3.3l-0.9-0.1c-0.1,1.9,0,3-3.5,3c-0.5,0-1.1,0-1.7-0.1v-5.5c0-2,0-2.3,3.1-2.3c3.6,0,5.1,0.2,5.4,4.3l0.9-0.1c-0.4-2.2-0.5-3.1-0.5-5.1c-2.5,0.1-5,0.2-7.6,0.2c-2.1,0-4.3,0-6.7-0.2v0.9c0.4,0,0.8-0.1,1.1-0.1c1.5,0,1.8,0.4,1.8,2.4v12.7c0,2-0.4,2.4-1.7,2.4c-0.3,0-0.7,0-1.1-0.1v0.9c2.1-0.1,4.1-0.2,6.3-0.2c2.7,0,5.5,0.1,8.4,0.2c0.1-2,0.2-3.4,0.6-5.6l-1-0.1C64.1,686,62.8,686.4,58.4,686.4z"></path>
      </g>
      <path d="M389.2,674.9c-0.9-0.2-1.6-0.7-3.9-0.7c-3.1,0-4.8,1.9-4.8,3.8c0,4.5,7.2,3.1,7.2,6.6c0,1.3-1,2.4-3,2.4c-2.2,0-3.3-1.6-3.4-3.3l-0.9,0.1c0.2,1.3,0.2,2.4,0.2,3.3c1.2,0.5,2.7,0.7,4.2,0.7c2.5,0,5.3-1,5.3-3.9c0-4.8-7.3-3.4-7.3-6.7c0-1.2,1.1-2.2,2.7-2.2c2.7,0,3.1,2.4,3.1,3.2l0.9-0.1C389.2,676.9,389.2,676.2,389.2,674.9"></path>
      <path d="M375.1,671.2c1.1,0,1.7-0.8,1.7-1.7c0-0.8-0.6-1.7-1.7-1.7s-1.7,0.8-1.7,1.7C373.4,670.3,374,671.2,375.1,671.2"></path>
      <path d="M376.3,674.4c-1.3,0.4-2.4,0.4-3.6,0.4c-0.4,0-0.9,0-1.3-0.1v0.9c0.4-0.1,0.8-0.1,1.1-0.1c1.3,0,1.4,0.3,1.4,2v6.5c0,2.3-0.2,2.5-1.2,2.5c-0.2,0-0.5,0-0.7,0v0.9c1-0.1,2.2-0.2,3.3-0.2c1.2,0,2.3,0.1,3.4,0.2v-0.9c-0.2,0-0.8,0-1,0c-1.4,0-1.4-0.4-1.4-2.5V674.4z"></path>
      <path d="M363.9,677.4c0-1.5-0.2-2.3-0.3-3c-1.1,0.3-2.1,0.4-3.1,0.4c-0.4,0-0.8,0-1.2-0.1v0.9c0.4-0.1,0.8-0.1,1.1-0.1c1.2,0,1.6,0.5,1.6,2.9v5.6c0,2.3-0.2,2.5-1.2,2.5c-0.2,0-0.5,0-0.7,0v0.9c1-0.1,2.2-0.2,3.3-0.2c1.2,0,2.5,0.1,3.6,0.2v-0.9c-0.2,0-1,0-1.2,0c-1.4,0-1.4-0.4-1.4-2.5v-2.9c0-3.8,1.6-5.7,2.7-5.7c1.5,0,0.4,2.3,2.2,2.3c1,0,1.5-0.7,1.5-1.5c0-1.2-0.8-2-2.9-2c-1.6,0-3.2,1.4-3.8,3.2L363.9,677.4z"></path>
      <g>
        <path d="M358.2,685.5c0,0-0.5,0.9-1.3,0.9c-0.8,0-0.9-0.9-0.9-1.8v-5.9c0-2.8-1.5-4.5-5-4.5c-3,0-4.8,1.9-4.8,3.2c0,1.4,0.9,1.6,1.4,1.6c0.7,0,1.3-0.4,1.3-1.3c0-0.4-0.2-0.8-0.2-1.3c0-0.8,0.8-1.3,2.2-1.3c2.2,0,2.6,1.7,2.6,3.3v1.7c-6,0-7.9,2.2-7.9,4.1c0,1.4,0.7,3.5,4.1,3.5c3.5,0,4-2.7,4-2.7l0.1,0c0,1.8,0.9,2.7,2.1,2.7c0.9,0,1.9-0.5,2.7-1.6L358.2,685.5z M353.6,682.5c0,1.6-0.7,4.3-3,4.3c-2.2,0-2.2-2.5-2.2-2.6c0-2.8,2.4-3.3,5.3-3.3V682.5z"></path>
        <path d="M338.4,668c-3.5,0-2.9,0.2-4.5,0.2c-1.3,0-2.4-0.1-3.6-0.2v0.9c0.5-0.1,0.8-0.1,1.2-0.1c1.2,0,1.7,0.5,1.7,2.2V684c0,2.3-0.5,2.5-1.8,2.5c-0.3,0-0.6,0-1.1-0.1v0.9c1.4-0.1,2.7-0.2,4.1-0.2c1.4,0,2.7,0.1,4.1,0.2v-0.9c-0.4,0-0.7,0.1-1.1,0.1c-1.3,0-1.8-0.2-1.8-2.5v-5.4c0.5,0.1,1,0.1,1.4,0.1c6.5,0,7.8-3.2,7.8-5.5C344.8,671,343.7,668,338.4,668z M337.1,677.8c-0.5,0-1,0-1.6-0.1v-6.5c0-2,0.2-2.3,2.4-2.3c2.3,0,4.1,1.5,4.1,4.3C342.1,676,340.2,677.8,337.1,677.8z"></path>
      </g>
      
      <!-- Text Elements -->
      <text xmlns="http://www.w3.org/2000/svg" dominant-baseline="middle" text-anchor="middle" class="cls-5 st6 st9 st10" id="text-top" x="220" y="160"></text>
      <text xmlns="http://www.w3.org/2000/svg" dominant-baseline="middle" text-anchor="middle" class="cls-6 st6 st7 st8" id="text-bottom" x="220" y="185"></text>
    </svg>
  `;

    const store = document.querySelector('pk-store');
    const svgContainer = document.getElementById('pk-svg');
    const loader = document.getElementById('pk-svg-loader');
    
    // Create loader to handle SVG state
    loader.style.display = 'flex';
    svgContainer.style.visibility = 'hidden';
    
    setTimeout(() => {
        // Reset SVG by default just to step 1 and 2
        if (store && (store.state.currentStep === 1 || store.state.currentStep === 2)) {
            // Replace SVG by defaut
            svgContainer.innerHTML = defaultSVGContent;
        }
    
        loader.style.display = 'none';
        svgContainer.style.visibility = 'visible';
    }, 1000);
});

/*
* Filter meta-products according to customer choice (step 1, 2 and 3)
*/
class FilterMetaProductsBySteps {
  constructor(products, state, step) {
    this.products = products;
    this.state = state;
    this.step = step;
  }

  filter() {
    const { selectedMethod, selectedFlavours, sensoryValues } = this.state;

    // Accents are removed from character strings to avoid errors
    const normalizeString = (str) => str.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const filteredProducts = this.products.filter(product => {
      
      // Method selection
      if (this.step >= 2 && selectedMethod) {
        const normalizedSelectedMethod = normalizeString(selectedMethod);

        if (typeof product.method === 'string') {
          const normalizedProductMethod = normalizeString(product.method);
      
          if (!normalizedProductMethod.includes(normalizedSelectedMethod)) return false;
        } else if (Array.isArray(product.method)) {
          const lowerCaseMethods = product.method.map(method => normalizeString(method));
      
          if (!lowerCaseMethods.includes(normalizedSelectedMethod)) return false;
        } else {
          return false;
        }
      }

      // Flavours selection
      if (this.step >= 3 && selectedFlavours && selectedFlavours.length > 0) {
        const normalizedSelectedFlavours = selectedFlavours.map(flavour => normalizeString(flavour));
        const normalizedProductFlavours = product.flavours.map(flavour => normalizeString(flavour));

        const allFlavoursIncluded = normalizedSelectedFlavours.every(flavour => normalizedProductFlavours.includes(flavour));
      
        if (!allFlavoursIncluded) return false;
      }

      return true;
    });

    // Sensory values selection, we calculate the weighted distance for each remaining product
    if (this.step >= 4 && sensoryValues && Object.keys(sensoryValues).length > 0) {
      const sensoryKeys = Object.keys(sensoryValues);
    
      // Function to calculate the weighted distance between a product's sensory values and the given sensory values
      const calculateWeightedDistance = (product, sensoryValues) => {
        // Reduce sensory keys to compute the sum of squared differences
        return sensoryKeys.reduce((sum, key) => {
          const stateValue = parseFloat(sensoryValues[key]); // Parse the sensory value from state
          const productValue = parseFloat(product.sensory[key]); // Parse the sensory value from product
    
          if (isNaN(stateValue) || isNaN(productValue)) {
            return sum;
          }
    
          // Calculate the difference between the state value and the product value
          const difference = stateValue - productValue;
          // Add the squared difference to the sum
          return sum + Math.pow(difference, 2);
        }, 0); // Initial sum is 0
      };
    
      let closestProduct = null;
      let minDistance = Infinity;

      filteredProducts.forEach(product => {
        // Calculate the weighted distance for the current product
        const weightedDistance = calculateWeightedDistance(product, sensoryValues);
    
        // If the calculated distance is less than the current minimum distance, update minDistance and closestProduct
        if (weightedDistance < minDistance) {
          minDistance = weightedDistance;
          closestProduct = product;
        }
      });
    
      // Return the closest product in an array if found, otherwise return an empty array
      return closestProduct ? [closestProduct] : [];
    }

    return filteredProducts;
  }
}

